/**
 * Compiles a song's text patterns (see types.ts for the notation) into a flat, sorted list of
 * note events on the step grid. Throws on anything malformed, so the tests catch every typo in
 * the score before anyone has to hear it. Pure.
 */

import { parseNote, placeAbove } from '../notes';
import { type Chord, chordSteps, voicing } from './chords';
import { type Compiled, type Drum, type NoteEvent, type Pattern, type Song, STEPS_PER_BAR } from './types';

const DRUMS: Readonly<Record<string, Drum>> = { K: 'kick', S: 'snare', H: 'hat' };
/** Groove letters -> semitones above the bass root (T, the third, is read from the chord). */
const GROOVE: Readonly<Record<string, number>> = { R: 0, O: 12, F: 7, f: -5 };

type Strike = { readonly note: number } | { readonly drum: Drum } | { readonly chord: readonly number[] };
type Resolved = Strike | 'hold' | 'rest';
/** The parts written as lines (a drum struck on the bass line becomes an event of its own part). */
type Line = 'lead' | 'alto' | 'bass' | 'arp';

/** A line's step tokens with its bar lines checked and removed. */
export function lineTokens(line: string): string[] {
  const out: string[] = [];
  for (const t of line.trim().split(/\s+/)) {
    if (t === '') continue;
    if (t === '|') {
      if (out.length % STEPS_PER_BAR !== 0) throw new Error(`bar line after ${out.length} steps`);
      continue;
    }
    out.push(t);
  }
  return out;
}

function common(t: string): Resolved | null {
  if (t === '.') return 'hold';
  if (t === '-') return 'rest';
  const drum = DRUMS[t];
  return drum ? { drum } : null;
}

function noteToken(t: string, drums: boolean): Resolved {
  const c = common(t);
  if (c !== null && (drums || typeof c === 'string')) return c;
  const note = parseNote(t);
  if (note === null) throw new Error(`bad token "${t}"`);
  return { note };
}

/** A melodic line of `want` steps, or a complaint about its length. */
function melody(line: string, name: string, want: number, drums: boolean): Resolved[] {
  const steps = lineTokens(line).map((t) => noteToken(t, drums));
  if (steps.length !== want) throw new Error(`${name} has ${steps.length} steps, chords ${want}`);
  return steps;
}

/** A groove bar applied to every bar of the chart. A hold across a chord change restrikes on the new root. */
function grooveLine(groove: string, chords: readonly (Chord | null)[], floor: number): Resolved[] {
  const bar = lineTokens(groove);
  if (bar.length !== STEPS_PER_BAR) throw new Error(`groove "${groove}" is not one bar`);
  let letter = '';
  return chords.map((chord, i): Resolved => {
    let t = bar[i % STEPS_PER_BAR] ?? '-';
    if (t === '.' && i > 0 && chord !== chords[i - 1] && letter !== '') t = letter;
    const c = common(t);
    if (c !== null) {
      if (c !== 'hold') letter = '';
      return c;
    }
    letter = t;
    if (!chord) return 'rest';
    const root = placeAbove(chord.root, floor);
    const up = t === 'T' ? chord.intervals[1] : GROOVE[t];
    if (up === undefined) throw new Error(`bad groove token "${t}"`);
    return { note: root + up };
  });
}

/** Arpeggio strikes: on the rhythm's `x`, and when the chord changes unless the rhythm is resting. */
function arpLine(rhythm: string | undefined, chords: readonly (Chord | null)[], floor: number): Resolved[] {
  const bar = rhythm === undefined ? null : lineTokens(rhythm);
  if (bar && bar.length !== STEPS_PER_BAR) throw new Error(`arp rhythm "${rhythm}" is not one bar`);
  let resting = false;
  return chords.map((chord, i): Resolved => {
    const t = bar ? (bar[i % STEPS_PER_BAR] ?? '.') : '.';
    if (t !== 'x' && t !== '.' && t !== '-') throw new Error(`bad arp token "${t}"`);
    if (t === '-') resting = true;
    if (t === 'x') resting = false;
    if (!chord || resting) return 'rest';
    const changed = t === 'x' || i === 0 || chord !== chords[i - 1] || !chords[i - 1];
    return changed ? { chord: voicing(chord, floor) } : 'hold';
  });
}

/** Resolved steps -> events. Each strike sounds until the next token that is not a hold. */
function toEvents(line: readonly Resolved[], part: Line, offset: number, out: NoteEvent[]): void {
  let from = -1;
  let strike: Strike | null = null;
  const close = (end: number): void => {
    if (!strike) return;
    const at = offset + from;
    const len = end - from;
    if ('drum' in strike) out.push({ part: 'drum', step: at, len, notes: [], drum: strike.drum });
    else if ('chord' in strike) out.push({ part, step: at, len, notes: strike.chord });
    else out.push({ part, step: at, len, notes: [strike.note] });
    strike = null;
  };
  line.forEach((r, i) => {
    if (r === 'hold') return;
    close(i);
    if (r === 'rest') return;
    from = i;
    strike = r;
  });
  close(line.length);
}

/** One pattern's events, appended at `offset`; returns its length in steps. */
export function compilePattern(p: Pattern, song: Song, offset: number, out: NoteEvent[]): number {
  const chords = chordSteps(p.chords);
  const want = chords.length;
  toEvents(melody(p.lead, 'lead', want, false), 'lead', offset, out);
  if (p.alto !== undefined) toEvents(melody(p.alto, 'alto', want, false), 'alto', offset, out);
  const bass = typeof p.bass === 'string'
    ? melody(p.bass, 'bass', want, true)
    : grooveLine(p.bass.groove, chords, song.bassFloor);
  toEvents(bass, 'bass', offset, out);
  toEvents(arpLine(p.arp, chords, song.arpFloor), 'arp', offset, out);
  return want;
}

export function compileSong(song: Song): Compiled {
  const events: NoteEvent[] = [];
  let steps = 0;
  for (const name of song.order) {
    const p = song.patterns[name];
    if (!p) throw new Error(`no pattern "${name}"`);
    try {
      steps += compilePattern(p, song, steps, events);
    } catch (e) {
      throw new Error(`pattern ${name}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  events.sort((a, b) => a.step - b.step);
  return { bpm: song.bpm, loop: song.loop, steps, mix: song.mix, events };
}
